import type {Course} from '@goschool/model'
import {Avatar, Card, CardActionArea, CardActions, CardHeader, Skeleton, styled} from '@mui/material'
import {GoSchool} from '@goschool/routing'
import {Link as RouterLink} from 'react-router-dom'
import {useCourseContext} from '@goschool/dao'
import {InstructorActionLink, StudentActionLink} from './CourseActions'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'
import SettingsIcon from '@mui/icons-material/Settings'
import {useTranslation} from 'react-i18next'
import {useMemo} from 'react'


export function CourseCard() {
  const {courseSnapshot} = useCourseContext()
  const course = useMemo(() => courseSnapshot.data(), [courseSnapshot])

  const instructorNames = useMemo(
    () => {
      return course.instructors.map(instructor => instructor.name).join(', ')
    },
    [course]
  )

  return <StyledCourseCard variant="elevation">
    <CardActionArea
      component={RouterLink}
      to={GoSchool.courseChat(courseSnapshot.ref)}>
      <CardHeader avatar={<CourseAvatar course={course}/>} title={course.title}
        subheader={instructorNames ?? <Skeleton/>}
      />
    </CardActionArea>

    <CardActions
      disableSpacing><CourseActions/></CardActions>
  </StyledCourseCard>
}


function CourseActions() {
  const {courseSnapshot, courseRoles} = useCourseContext()
  const isInstructor = useMemo(
    () => courseRoles.includes('instructor') || courseRoles.includes('admin'),
    [courseRoles]
  )
  const {t} = useTranslation()
  if (isInstructor) {
    return <>
      <InstructorActionLink
        color="inherit" title={t('course:actions.studentChats')} edge="end"
        to={GoSchool.courseChats(courseSnapshot.ref)}
        Icon={ForumOutlinedIcon}/>
      <InstructorActionLink
        color="inherit" title={t('course:actions.documents')}
        to={GoSchool.courseDocuments(courseSnapshot.ref)}
        Icon={LibraryBooksIcon}/>
      <InstructorActionLink
        color="inherit" title={t('course:actions.details')} to={GoSchool.course(courseSnapshot.ref)}
        Icon={SettingsIcon}/>
    </>
  } else {
    return <>
      <StudentActionLink
        color="inherit" title={t('course:actions.documents')} edge="end"
        to={GoSchool.courseDocuments(courseSnapshot.ref)}
        Icon={LibraryBooksIcon}/>
      <StudentActionLink
        color="inherit" title={t('course:actions.details')} to={GoSchool.course(courseSnapshot.ref)}
        Icon={SettingsIcon}/>
    </>
  }
}

function CourseAvatar({course}: { course: Course }) {
  if (course.icon != null) {
    return <Avatar src={course.icon}/>
  } else if (course.banner != null) {
    return <Avatar src={course.banner}/>
  } else {
    return <Avatar>{course.title.slice(0, 2).toUpperCase()}</Avatar>
  }
}

const StyledCourseCard = styled(Card, {name: 'StyledCourseCard', slot: 'Root'})(
  ({theme}) => ({
    '& .MuiCardHeader-root': {
      height: '6em',
      '& .MuiAvatar-root': {
        width: theme.spacing(8),
        height: theme.spacing(8)
      }
    },
    '& .MuiCardActions-root': {
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'stretch'
    }
  })
)
